/**
 * Lightweight toast for silent confirmations ("Code copied", "Saved", …).
 * Single-toast queue, auto-dismiss. Sits above the bottom nav. No deps.
 *
 * Usage: const toast = useToast(); toast('Saved');
 */
import { createContext, useCallback, useContext, useRef, useState, type ReactNode } from 'react';

type ShowToast = (message: string) => void;

const Ctx = createContext<ShowToast | null>(null);

export function ToastProvider({ children }: { children: ReactNode }): JSX.Element {
  const [message, setMessage] = useState<string | null>(null);
  const timer = useRef<number | undefined>(undefined);

  const show = useCallback<ShowToast>((msg) => {
    setMessage(msg);
    window.clearTimeout(timer.current);
    timer.current = window.setTimeout(() => setMessage(null), 2200);
  }, []);

  return (
    <Ctx.Provider value={show}>
      {children}
      {message !== null && (
        <div
          className="pointer-events-none fixed inset-x-0 z-50 flex justify-center px-4"
          style={{ bottom: 'calc(76px + env(safe-area-inset-bottom, 0))' }}
          role="status"
          aria-live="polite"
        >
          <div className="animate-slide-up max-w-md rounded-card bg-brand-navy px-4 py-2.5 text-sm font-medium text-brand-cream shadow-lg">
            {message}
          </div>
        </div>
      )}
    </Ctx.Provider>
  );
}

/** Returns a `show(message)` function. No-ops outside a ToastProvider. */
export function useToast(): ShowToast {
  return useContext(Ctx) ?? (() => undefined);
}
